/**
 * Add a role to the account. A homeowner picks one professional role; a
 * professional is offered homeowner. Confirming adds the role, makes it
 * active and opens its onboarding.
 */

import type { UserType } from "@shared/types";
import { Loader2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { StudioButton } from "@/features/studio/theme";
import { cn } from "@/lib/utils";
import { useRoleActions } from "../hooks/use-role-actions";

const PROFESSIONAL_ROLES: readonly UserType[] = [
	"designer",
	"contractor",
	"realtor",
];

const isProfessional = (role: UserType): boolean =>
	PROFESSIONAL_ROLES.includes(role);

export interface AddRoleDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The roles the account may add. */
	addableRoles: UserType[];
	/** Roles whose setup was started and left unfinished. */
	unfinishedRoles: UserType[];
	/** Where focus goes when the dialog closes. */
	returnFocusTo?: React.RefObject<HTMLElement | null>;
}

export function AddRoleDialog({
	open,
	onOpenChange,
	addableRoles,
	unfinishedRoles,
	returnFocusTo,
}: AddRoleDialogProps): React.JSX.Element {
	const { t } = useTranslation();
	const groupName = useId();
	const { pendingRole, add } = useRoleActions();
	const onlyChoice = addableRoles.length === 1 ? addableRoles[0] : null;
	const [picked, setPicked] = useState<UserType | null>(onlyChoice);

	// Each opening starts from the same choice.
	useEffect(() => {
		if (open) setPicked(onlyChoice);
	}, [open, onlyChoice]);

	const roleName = (role: UserType): string =>
		t(`roleSession.roleNames.${role}`);
	const resumes = picked !== null && unfinishedRoles.includes(picked);
	const discarded =
		picked !== null && isProfessional(picked)
			? (unfinishedRoles.find(
					(role) => role !== picked && isProfessional(role),
				) ?? null)
			: null;
	const busy = pendingRole !== null;

	const confirm = async (): Promise<void> => {
		if (!picked || busy) return;
		const added = await add(picked);
		if (added) onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
			<DialogContent
				className="rounded-xl border-dashboard-border bg-dashboard-bg text-dashboard-text"
				onCloseAutoFocus={(event) => {
					const target = returnFocusTo?.current;
					if (target?.isConnected) {
						event.preventDefault();
						target.focus();
					}
				}}
			>
				<DialogHeader>
					<DialogTitle className="font-display text-xl font-semibold text-dashboard-text">
						{resumes && picked
							? t("roleSwitcher.addDialog.continueTitle", {
									role: roleName(picked),
								})
							: t("roleSwitcher.addDialog.title")}
					</DialogTitle>
					<DialogDescription className="text-sm text-dashboard-muted">
						{picked
							? t("roleSwitcher.addDialog.consequence", {
									role: roleName(picked),
								})
							: t("roleSwitcher.addDialog.choose")}
						{picked && isProfessional(picked)
							? ` ${t("roleSwitcher.addDialog.professionalReview")}`
							: ""}
					</DialogDescription>
				</DialogHeader>

				{addableRoles.length > 1 && (
					<fieldset className="space-y-2">
						<legend className="sr-only">
							{t("roleSwitcher.addDialog.chooseLabel")}
						</legend>
						{addableRoles.map((role) => (
							<label
								key={role}
								className={cn(
									"flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors",
									picked === role
										? "border-dashboard-accent bg-dashboard-paper text-dashboard-text"
										: "border-dashboard-border bg-dashboard-bg text-dashboard-muted-dark hover:bg-dashboard-surface",
								)}
							>
								<input
									type="radio"
									name={groupName}
									value={role}
									checked={picked === role}
									disabled={busy}
									onChange={() => setPicked(role)}
									className="h-4 w-4 accent-dashboard-accent"
								/>
								{roleName(role)}
							</label>
						))}
					</fieldset>
				)}

				{discarded && (
					<p className="rounded-md border border-dashboard-warning-border bg-dashboard-warning-bg-soft px-3 py-2 text-sm text-dashboard-warning-strong">
						{t("roleSwitcher.addDialog.discard", {
							role: roleName(discarded),
						})}
					</p>
				)}

				<DialogFooter className="gap-2">
					<StudioButton
						variant="secondary"
						size="md"
						type="button"
						disabled={busy}
						onClick={() => onOpenChange(false)}
					>
						{t("roleSwitcher.addDialog.cancel")}
					</StudioButton>
					<StudioButton
						variant="primary"
						size="md"
						type="button"
						disabled={!picked || busy}
						onClick={() => void confirm()}
					>
						{busy && (
							<Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
						)}
						{resumes
							? t("roleSwitcher.addDialog.confirmContinue")
							: picked
								? t("roleSwitcher.addDialog.confirm", {
										role: roleName(picked),
									})
								: t("roleSwitcher.addRole")}
					</StudioButton>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
